import { alpha } from '@mui/material'

export const CLIENT_STATUS = {
  DELETED: 'deleted',
  BLOCKED: 'blocked',
  PROSPECT: 'prospect',
  SUSPENDED: 'suspended',
  MEMBERSHIP: 'membership',
  PACKAGE: 'package',
  INTRO: 'intro',
  PENDING: 'pending',
  ALUMNI: 'alumni',
  AGGREGATOR: 'aggregator',
  LEAD: 'lead',
}

export const getStatusStyles = (status) => {
  const statusColorMap = {
    [CLIENT_STATUS.DELETED]: { bgcolor: 'grey.200', color: 'grey.700' },
    [CLIENT_STATUS.BLOCKED]: { bgcolor: 'error.lightest', color: 'error.dark' },
    [CLIENT_STATUS.PROSPECT]: { bgcolor: 'grey.100', color: 'grey.800' },
    [CLIENT_STATUS.SUSPENDED]: { bgcolor: 'warning.lightest', color: 'warning.dark' },
    [CLIENT_STATUS.MEMBERSHIP]: { bgcolor: 'success.lightest', color: 'success.dark' },
    [CLIENT_STATUS.PACKAGE]: { bgcolor: 'blue.lightest', color: 'blue.dark' },
    [CLIENT_STATUS.INTRO]: { bgcolor: 'secondary.lightest', color: 'secondary.dark' },
    [CLIENT_STATUS.PENDING]: { bgcolor: 'warning.lightest', color: 'warning.dark' },
    [CLIENT_STATUS.AGGREGATOR]: { bgcolor: 'orange.lightest', color: 'orange.dark' },
    [CLIENT_STATUS.ALUMNI]: { bgcolor: 'grey.100', color: 'grey.700' },
    [CLIENT_STATUS.LEAD]: { bgcolor: 'orange.lightest', color: 'orange.dark' },
    default: { bgcolor: 'grey.100', color: 'grey.700' },
  }
  return statusColorMap[status] || statusColorMap.default
}

export const getClientNameStyle = (isSelected, styleBase) => {
  if (isSelected) {
    return {
      ...styleBase,
      color: 'primary.dark',
    }
  }
  return styleBase
}

export const getStatusBadgeStyle = (status, styleBase) => {
  const statusColors = getStatusStyles(status)
  return {
    ...styleBase,
    bgcolor: statusColors.bgcolor,
    color: statusColors.color,
  }
}

export const getAvatarStyle = (clientId, baseStyle, getAvatarColorFn) => ({
  ...baseStyle,
  bgcolor: getAvatarColorFn(clientId),
})

export const secondaryActionStyles = {
  color: 'grey.700',
  backgroundColor: 'black',
  borderRadius: '24px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}

export const customHeaderStyles = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'column', md: 'row' },
  justifyContent: { sm: 'space-between' },
  alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
  marginBottom: { xs: 2, sm: 2, md: 2 },
  paddingX: { xs: 2, sm: 'unset' },
  width: '100%',
  gap: { xs: 1, sm: 1, md: 2 },
  '& > div:first-of-type': {
    width: '100%',
    flex: { md: 1 },
    minWidth: 0,
    '& .MuiTextField-root': {
      width: '100% !important',
    },
    '& .MuiSvgIcon-root': {
      color: 'green.dark !important',
    },
  },
}

export const corporateLookupButtonStyles = {
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: 'primary.main',
  border: 'none',
  padding: { xs: '6px 8px', sm: '8px 12px' },
  fontSize: 'body2.fontSize',
  fontWeight: 'bold',
  minHeight: 'auto',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'primary.dark',
    boxShadow: 'none',
    textDecoration: 'underline',
  },
}

export const corporateLookupButtonContainer = {
  display: 'flex',
  alignItems: 'center',
  ml: 1,
  flexShrink: 0,
  minWidth: 'fit-content',
}

export const selectingAllBackdropStyles = {
  zIndex: (theme) => theme.zIndex.modal - 1,
}

// Corporate ("All locations") search checkbox on the right side of the clients search input (OC-636)
export const corporateSearchToggleStyles = {
  root: {
    ml: 1,
    mr: 0,
    pr: 2,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '& .MuiFormControlLabel-label': {
      fontSize: 'body2.fontSize',
      color: 'text.secondary',
    },
  },
  labelContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
  },
  infoIcon: {
    fontSize: '1rem',
    color: 'text.secondary',
  },
}

export const customActionsStyles = {
  container: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
    width: { xs: '100%', sm: '100%', md: 'auto' },
    flexDirection: { xs: 'row-reverse', sm: 'row-reverse', md: 'row' },
    px: { xs: 0, sm: 'unset', md: 'unset' },
    mx: { xs: 0, sm: 0, md: 0 },
    pt: { xs: 0.5, sm: 'unset', md: 'unset' },
    mb: { xs: 2, sm: 0, md: 0 },
  },
  divider: {
    display: { xs: 'none', md: 'block' },
    height: 32,
    borderColor: 'divider',
    ml: 2,
    mr: 2,
  },
  filterButton: {
    minWidth: 'auto !important',
    minHeight: 'auto !important',
    padding: '8px !important',
    borderRadius: '24px',
    backgroundColor: 'background.paper',
    color: 'grey.600',
    border: '1px solid',
    borderColor: 'grey.300',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'grey.50',
      borderColor: 'grey.400',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      margin: '0 !important',
    },
  },
  addClientButton: {
    textTransform: 'none',
    borderRadius: '24px',
    backgroundColor: 'primary.main',
    color: 'white',
    padding: '8px 16px',
    boxShadow: 'none',
    border: 'none',
    flex: { xs: 1, sm: 1, md: 'none' },
    fontSize: 'body2.fontSize',
    '&:hover': {
      opacity: 0.9,
      boxShadow: 'none',
    },
  },
}

export const clientsPageStyles = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    height: 64,
    display: 'flex',
    alignItems: 'center',
    px: 2,
    borderBottom: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
  },
  tabsContainer: {
    bgcolor: 'background.paper',
  },
  tabsWrapper: {
    px: 3,
  },
  tabsInner: {
    display: 'flex',
    gap: 0.5,
    px: 2,
    py: 1.5,
    alignItems: 'center',
  },
  manageButton: {
    textTransform: 'none',
    fontWeight: 'medium',
    borderRadius: { xs: '50%', sm: 1 },
    minWidth: { xs: 'auto', sm: 'auto' },
    px: { xs: 1, sm: 2 },
    py: 0.75,
    fontSize: 'body2.fontSize',
    border: 'none',
    boxShadow: 'none',
    color: 'text.secondary',
    bgcolor: 'transparent',
    '&:hover': {
      bgcolor: 'action.hover',
      color: 'primary.main',
    },
  },
  settingsIcon: {
    fontSize: 'h4.fontSize',
  },
  tabsLoading: {
    display: 'flex',
    alignItems: 'center',
    px: 2,
  },
  tabsLoadingInner: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'scale(0.5)',
    transformOrigin: 'center',
  },
  tabButton: (isActive) => ({
    textTransform: 'none',
    fontWeight: isActive ? 'semiBold' : 'medium',
    borderRadius: 1,
    minWidth: 'auto',
    px: 2,
    py: 0.75,
    fontSize: 'body2.fontSize',
    border: 'none',
    boxShadow: 'none',
    ...(isActive
      ? {
          bgcolor: 'primary.lightest',
          color: 'primary.main',
          '&:hover': {
            color: 'primary.darkest',
          },
        }
      : {
          color: 'text.secondary',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
            color: 'primary.main',
          },
        }),
  }),
  content: {
    flex: 1,
    px: { xs: 2, sm: 5 },
    pt: 1.5,
    pb: 2,
    overflow: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  actions: {
    container: {
      display: 'flex',
      justifyContent: 'right',
      alignItems: 'center',
      mb: 2,
      px: 2,
    },
    filterButton: {
      width: 131,
      height: 40,
      borderRadius: '24px',
      padding: '8px 20px 8px 12px',
      backgroundColor: 'background.paper',
      color: 'green.main',
      borderColor: 'green.main',
      textTransform: 'none',
      mr: 1,
      '&:hover': {
        backgroundColor: 'green.lightest',
        borderColor: 'green.main',
        color: 'green.main',
      },
      '& .MuiButton-startIcon': {
        marginRight: 1,
      },
    },
    addButton: {
      width: 131,
      height: 40,
      borderRadius: '24px',
      padding: '8px 20px 8px 12px',
      backgroundColor: 'green.main',
      color: 'black',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: 'green.main',
      },
      '& .MuiButton-startIcon': {
        marginRight: 1,
      },
    },
    divider: {
      height: 32,
      borderColor: 'divider',
      ml: 2,
      mr: 3,
    },
  },
  tabDivider: {
    height: 24,
    mx: 1,
    borderColor: 'divider',
  },
  table: {
    '& .MuiTableHead-root': {
      '& .MuiTableCell-root': {
        bgcolor: 'grey.50',
        borderBottom: 1,
        borderColor: 'divider',
        fontWeight: 'semiBold',
        fontSize: 'caption.fontSize',
        color: 'green.main',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&.selected': {
          bgcolor: 'action.selected',
          '& .MuiTableRow-root:has(.MuiCheckbox-root.Mui-checked)': {
            bgcolor: 'action.selected',
            '& .client-name': {
              color: 'green.dark !important',
            },
            '& .phone-value': {
              color: 'green.dark !important',
            },
            '& .barcode-value': {
              color: 'green.dark !important',
            },
          },
        },
      },
      '& .MuiTableCell-root': {
        borderBottom: 1,
        borderColor: 'divider',
        fontSize: 'body2.fontSize',
      },
    },
  },
  emptyState: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      textAlign: 'center',
      py: 8,
      px: 4,
      minHeight: '400px',
      width: '100%',
    },
    iconContainer: {
      width: 64,
      height: 64,
      borderRadius: '50%',
      backgroundColor: 'orange.lightest',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      mb: 3,
    },
    icon: {
      fontSize: 'h1.fontSize',
      color: 'orange.dark',
      backgroundColor: 'orange.lightest',
      width: 32,
      height: 32,
    },
    title: {
      mb: 1,
      fontWeight: 'semiBold',
    },
    description: {
      mb: 4,
      maxWidth: 400,
    },
    button: {
      minWidth: { xs: 'auto', sm: 'auto', md: '120px' },
      borderRadius: '24px',
      backgroundColor: 'primary.main',
      color: 'white',
      textTransform: 'none',
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'primary.main',
      '&:hover': {
        opacity: 0.9,
        boxShadow: 'none',
      },
      '& .MuiButton-startIcon': {
        marginRight: 1,
      },
    },
  },
  mobileTable: {
    scrollContainer: {
      maxHeight: 'calc(100vh - 200px)',
      overflow: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    container: {
      borderTop: 'none',
      borderLeft: '1px solid',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'divider',
      borderBottomLeftRadius: 1,
      borderBottomRightRadius: 1,
      bgcolor: 'background.paper',
    },
    row: {
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    rowContent: {
      display: 'flex',
      alignItems: 'center',
      p: 2,
      gap: { xs: 0.5, sm: 1 },
      '&:hover': { bgcolor: 'grey.50' },
    },
    clientInfo: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    },
    clientInfoCentered: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    clientInfoTop: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
    clientName: {
      fontSize: 'body2.fontSize',
    },
    clientNameClickable: {
      fontSize: 'body2.fontSize',
      cursor: 'pointer',
      display: 'block',
      color: 'inherit',
      textDecoration: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: 'green.main',
        textDecoration: 'underline',
      },
    },
    clientNameClickableSelected: {
      fontSize: 'body2.fontSize',
      cursor: 'pointer',
      color: 'green.dark',
      '&:hover': {
        color: 'green.dark',
        textDecoration: 'underline',
      },
    },
    alertsContainer: {
      mt: 0.25,
    },
    chevron: {
      ml: 1,
      cursor: 'pointer',
      p: 0.5,
      borderRadius: 1,
      '&:hover': { bgcolor: 'grey.100' },
      transition: 'transform 0.2s ease',
      color: 'text.secondary',
    },
    chevronExpanded: {
      ml: 1,
      cursor: 'pointer',
      p: 0.5,
      borderRadius: 1,
      '&:hover': { bgcolor: 'grey.100' },
      transition: 'transform 0.2s ease',
      color: 'text.secondary',
      transform: 'rotate(180deg)',
    },
    chevronCollapsed: {
      ml: 1,
      cursor: 'pointer',
      p: 0.5,
      borderRadius: 1,
      '&:hover': { bgcolor: 'grey.100' },
      transition: 'transform 0.2s ease',
      color: 'text.secondary',
      transform: 'rotate(0deg)',
    },
    expandedContent: {
      bgcolor: 'grey.25',
      borderTop: '1px solid',
      borderColor: 'divider',
    },
    detailRow: {
      display: 'flex',
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
    detailRowLast: {
      display: 'flex',
      borderBottom: 'none',
    },
    detailLabel: {
      bgcolor: 'grey.200',
      px: 2,
      py: 1.5,
      width: { xs: 80, sm: 100 },
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    },
    detailLabelText: {
      variant: 'body2',
      color: 'text.secondary',
      fontWeight: 'medium',
      whiteSpace: 'nowrap',
    },
    detailValue: {
      px: 2,
      py: 1.5,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
    },
    detailValueText: {
      variant: 'body2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    },
    statusBadge: {
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: 'inline-block',
    },
    statusBadgeClickable: {
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: 'inline-block',
      cursor: 'pointer',
      fontSize: 'body2.fontSize',
      fontWeight: 'semiBold',
      '&:hover': {
        opacity: 0.8,
      },
    },
    card: {
      mb: 2,
      p: 2,
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 1,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      mb: 2,
    },
    avatar: {
      mr: 2,
    },
    clientEmail: {
      variant: 'body2',
      color: 'text.secondary',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    alerts: {
      mr: 2,
    },
    details: {
      display: 'grid',
      gap: 1,
    },
    loadingIndicator: {
      display: 'flex',
      justifyContent: 'center',
      p: 2,
    },
  },

  tabletTable: {
    container: {
      borderTop: 'none',
      borderLeft: '1px solid',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'divider',
      borderBottomLeftRadius: 1,
      borderBottomRightRadius: 1,
      bgcolor: 'background.paper',
    },
    row: {
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    rowContent: {
      display: 'flex',
      alignItems: 'center',
      p: 2,
      '&:hover': { bgcolor: 'grey.50' },
    },
    checkbox: {
      mr: 2,
      flexShrink: 0,
    },
    avatar: {
      mr: 2,
      width: 40,
      height: 40,
      flexShrink: 0,
    },
    avatarDynamic: (avatarColor) => ({
      mr: 2,
      width: 40,
      height: 40,
      bgcolor: avatarColor || 'primary.main',
      flexShrink: 0,
    }),
    clientInfo: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0%',
      width: 10,
      minWidth: 0,
      overflow: 'hidden',
    },
    clientName: {
      fontWeight: 'bold',
      mb: 0.5,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    clientNameClickable: {
      fontWeight: 'bold',
      mb: 0.5,
      cursor: 'pointer',
      display: 'block',
      color: 'inherit',
      textDecoration: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: 'green.main',
        textDecoration: 'underline',
      },
    },
    clientNameClickableSelected: {
      fontWeight: 'bold',
      mb: 0.5,
      cursor: 'pointer',
      color: 'green.dark',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: 'green.dark',
        textDecoration: 'underline',
      },
    },
    clientEmail: {
      color: 'text.secondary',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    alerts: {
      mr: 2,
      flexShrink: 0,
    },
    chevron: {
      ml: 1,
      cursor: 'pointer',
      p: 0.5,
      borderRadius: '50%',
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': { bgcolor: 'grey.100' },
      transition: 'transform 0.2s ease',
      color: 'text.secondary',
      flexShrink: 0,
    },
    chevronExpanded: {
      ml: 1,
      cursor: 'pointer',
      p: 0.5,
      borderRadius: '50%',
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': { bgcolor: 'grey.100' },
      transition: 'transform 0.2s ease',
      color: 'text.secondary',
      transform: 'rotate(180deg)',
      flexShrink: 0,
    },
    chevronCollapsed: {
      ml: 1,
      cursor: 'pointer',
      p: 0.5,
      borderRadius: '50%',
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': { bgcolor: 'grey.100' },
      transition: 'transform 0.2s ease',
      color: 'text.secondary',
      transform: 'rotate(0deg)',
      flexShrink: 0,
    },
    expandedContent: {
      bgcolor: 'grey.25',
      borderTop: '1px solid',
      borderColor: 'divider',
    },
    detailRow: {
      display: 'flex',
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
    detailRowLast: {
      display: 'flex',
      borderBottom: 'none',
    },
    detailLabel: {
      bgcolor: 'grey.200',
      px: 2,
      py: 1.5,
      width: { xs: 80, sm: 100 },
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    },
    detailLabelText: {
      variant: 'body2',
      color: 'text.secondary',
      fontWeight: 'medium',
      whiteSpace: 'nowrap',
    },
    detailValue: {
      px: 2,
      py: 1.5,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
    },
    detailValueText: {
      variant: 'body2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    },
    statusBadge: {
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: 'inline-block',
    },
    statusBadgeClickable: {
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: 'inline-block',
      cursor: 'pointer',
      fontSize: 'body2.fontSize',
      fontWeight: 'semiBold',
      '&:hover': {
        opacity: 0.8,
      },
    },
    loadingIndicator: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 2,
      marginTop: 1,
    },
  },
}

export const clientsTableStyles = {
  checkbox: {
    '&.Mui-checked': {
      color: 'green.dark',
    },
    '&.MuiCheckbox-indeterminate': {
      color: 'green.dark',
    },
    '&:hover': {
      backgroundColor: 'action.hover',
      '&.Mui-checked': {
        color: 'green.dark',
      },
    },
    '& .MuiSvgIcon-root': {
      fontSize: 'h6.fontSize',
    },
  },
  phone: {
    typography: {
      color: 'gray.70',
    },
    typographySelected: {
      color: 'green.dark',
    },
  },
  barcode: {
    typography: {
      color: 'gray.70',
      fontFamily: 'monospace',
    },
    typographySelected: {
      color: 'green.dark',
      fontFamily: 'monospace',
    },
  },
  clientName: {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      cursor: 'pointer',
      minWidth: 0,
      overflow: 'hidden',
      color: 'inherit',
      textDecoration: 'none',
      '&:hover': {
        '& .client-name': {
          color: 'green.dark',
          textDecoration: 'underline',
        },
      },
    },
    avatar: {
      width: 40,
      height: 40,
      fontSize: 'body1.fontSize',
      fontWeight: 'semiBold',
      flexShrink: 0,
    },
    textContainer: {
      minWidth: 0,
      overflow: 'hidden',
      flex: 1,
    },
    name: {
      transition: 'all 0.2s ease',
      color: 'gray.80',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      width: '100%',
    },
    nameSelected: {
      transition: 'all 0.2s ease',
      color: 'green.dark',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      width: '100%',
    },
    email: {
      color: 'gray.60',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      width: '100%',
    },
    emailSelected: {
      color: 'green.dark',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      width: '100%',
    },
  },

  alerts: {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    count: {
      ml: 1,
    },
    alertBox: (alert, index) => ({
      width: 32,
      height: 32,
      borderRadius: '40px',
      backgroundColor: alert.color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: index + 1,
      marginLeft: index > 0 ? '-10px' : '0px',
      border: '2px solid white',
      padding: '8px',
    }),
    mobileAlertBox: (alert, index) => ({
      width: 24,
      height: 24,
      borderRadius: '40px',
      backgroundColor: alert.color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: index + 1,
      marginLeft: index > 0 ? '-8px' : '0px',
      border: '2px solid white',
      padding: '4px',
    }),
    alertIcon: (iconColor) => ({
      fontSize: 'h6.fontSize',
      color: iconColor,
    }),
    mobileAlertIcon: (iconColor) => ({
      fontSize: 'body2.fontSize',
      color: iconColor,
    }),
  },
  // "Copy to this location" action on corporate-search rows not at the current site (OC-636)
  copyToLocationButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    borderRadius: '999px',
  },
  corporateRowActions: {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
  },
  // Location + Status cells in corporate ("All locations") search mode (OC-636).
  // stack/stackRow are shared by BOTH columns so their lines align 1:1.
  location: {
    stack: {
      display: 'flex',
      flexDirection: 'column',
      py: 0.5,
    },
    stackRow: {
      height: 28,
      display: 'flex',
      alignItems: 'center',
    },
    typography: {
      fontSize: 'body2.fontSize',
      color: 'text.primary',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  actions: {
    container: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    menu: {
      overflow: 'visible',
      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
      mt: 1.5,
      '& .MuiAvatar-root': {
        width: 32,
        height: 32,
        ml: -0.5,
        mr: 1,
      },
      '&:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 0,
        right: 14,
        width: 80,
        height: 80,
        bgcolor: 'background.paper',
        transform: 'translateY(-50%) rotate(45deg)',
        zIndex: 0,
      },
    },
    menuPaper: {
      minWidth: 160,
    },
    mobileDrawer: {
      '& .MuiDrawer-paper': {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        maxHeight: '50vh',
      },
    },
    mobileDrawerContent: {
      p: 1,
      pb: 1,
    },
    mobileMenuItem: {
      py: 2,
      px: 0,
      mx: 0,
      borderRadius: 0,
      fontSize: 'body2.fontSize',
      width: '100%',
      '&:hover': {
        bgcolor: 'action.hover',
      },
    },
    mobileMenuIcon: {
      minWidth: 40,
      color: 'text.secondary',
    },
    mobileMenuText: {
      '& .MuiTypography-root': {
        fontSize: 'body2.fontSize',
        fontWeight: 'medium',
      },
    },
    menuDivider: {
      my: 1,
    },
    dangerText: {
      '& .MuiTypography-root': {
        color: 'error.main',
      },
    },
    dangerIcon: {
      color: 'error.main',
    },
  },
  status: {
    button: {
      width: 104,
      height: 32,
      fontWeight: 'semiBold',
      fontSize: 'body2.fontSize',
      lineHeight: '20px',
      letterSpacing: 0,
      textAlign: 'center',
      textTransform: 'none',
      borderRadius: 0.5,
      padding: '6px 12px',
      cursor: 'pointer',
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        opacity: 0.8,
      },
    },
    disabledButton: {
      width: 104,
      height: 32,
      fontWeight: 'semiBold',
      fontSize: 'body2.fontSize',
      lineHeight: '20px',
      letterSpacing: 0,
      textAlign: 'center',
      textTransform: 'none',
      cursor: 'default',
      borderRadius: 0.5,
      padding: '6px 12px',
      '&.Mui-disabled': {
        border: 'none',
        boxShadow: 'none',
      },
    },
  },
  phoneValidation: {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mt: 0.25,
    },
    icon: {
      fontSize: 'caption.fontSize',
      color: 'error.main',
    },
    text: {
      color: 'error.main',
      fontSize: 'caption.fontSize',
    },
  },
}

export const clientOverviewStyles = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    p: 3,
  },
  // OC-1205: `loading` is a flex ROW, which is right for a lone spinner but lays a
  // multi-part message out sideways. Message panels stack.
  messagePanel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    gap: 1,
    minHeight: '60vh',
    p: 3,
  },
  mergedBannerIcon: {
    fontSize: 48,
    color: 'text.secondary',
    mb: 1,
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    bgcolor: 'background.paper',
    borderBottom: 1,
    borderColor: 'divider',
    p: { xs: 2, sm: 3 },
  },
  headerActions: {
    display: 'flex',
    alignItems: { xs: 'flex-start', md: 'center' },
    justifyContent: 'space-between',
    mt: 2,
    gap: 2,
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    width: '100%',
  },
  headerProfile: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: '1 1 auto',
    minWidth: 0,
    flexWrap: { xs: 'wrap', sm: 'nowrap' },
  },
  headerBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
  },
  homeLocation: {
    ml: 2,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: 220,
    display: { xs: 'none', sm: 'flex' },
    flexDirection: 'column',
    overflow: 'hidden',
  },
  backButton: {
    mr: 1,
  },
  memberSinceSkeleton: {
    display: 'inline-block',
    verticalAlign: 'middle',
    width: 90,
  },
  avatar: (avatarColor) => ({
    width: 64,
    height: 64,
    fontSize: 'h4.fontSize',
    fontWeight: 'semiBold',
    bgcolor: avatarColor,
  }),
  avatarClickable: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  headerButtons: {
    display: 'flex',
    gap: 1,
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    justifyContent: { xs: 'flex-start', md: 'flex-end' },
    width: { xs: '100%', md: 'auto' },
    flexShrink: 0,
  },
  headerButton: {
    borderColor: 'divider',
    borderRadius: 5,
    flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
    minWidth: { xs: 0, sm: 'auto' },
  },
  mainContentFlex: {
    display: 'flex',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    minWidth: 0,
  },
  sidebar: {
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    bgcolor: 'background.paper',
    pl: 5,
    py: 2,
  },
  sidebarList: {
    p: 0,
    width: 300,
  },
  sidebarItem: {
    mt: 1,
    py: 1,
    px: 1.5,
    borderRadius: 1,
    gap: 2,
    '&.Mui-selected': {
      bgcolor: 'primary.lightest',
      '& .MuiSvgIcon-root': {
        color: 'primary.dark',
      },
      '& .MuiListItemText-root': {
        color: 'primary.dark',
        fontWeight: '600',
      },
    },
    '& .MuiListItemText-root': {
      margin: 0,
    },
  },
  sidebarIcon: {
    color: 'gray.64',
    width: 24,
    height: 24,
    minWidth: 'auto',
  },

  sidebarText: {
    fontSize: 'body1.fontSize',
  },
  content: {
    flex: 1,
    minWidth: 0,
    overflowX: 'hidden',
    p: { xs: 2, sm: 3 },
  },
  attendanceCard: {
    mb: 3,
  },
  attendanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    mb: 3,
    flexWrap: 'wrap',
    gap: 1,
  },
  attendanceTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
  },
  // Segmented "All / This location" scope toggle — both pills equal-sized in a subtle track
  attendanceScopeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    backgroundColor: 'grey.100',
    borderRadius: '999px',
    p: '3px',
  },
  attendanceScopeChip: (active) => ({
    height: 28,
    borderRadius: '999px !important',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    backgroundColor: active ? 'primary.main' : 'transparent',
    color: active ? 'common.white' : 'text.secondary',
    '& .MuiChip-label': { px: 1.5 },
    '&:hover': {
      backgroundColor: active ? 'primary.dark' : 'action.hover',
    },
  }),
  attendanceContent: {
    display: 'flex',
    gap: 4,
    flexDirection: { xs: 'column', sm: 'row' },
  },
  attendanceLastAttendance: {
    textAlign: 'right',
  },
  attendanceMetrics: {
    minWidth: 140,
    flexShrink: 0,
  },
  attendanceMetricItem: {
    mb: 2.5,
  },
  attendanceChartWrapper: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  attendanceChart: {
    container: {
      display: 'flex',
      width: '100%',
      height: '100%',
      minHeight: 200,
      gap: 1,
    },
    yAxis: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      pr: 1,
      pb: 3,
    },
    barsWrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      flex: 1,
      gap: 1.5,
      borderLeft: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'divider',
      pl: 1,
      pb: 0,
    },
    barContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1,
      justifyContent: 'flex-end',
      height: '100%',
      pb: 0.5,
    },
    countLabel: (isCurrentMonth) => ({
      color: isCurrentMonth ? 'primary.main' : 'text.secondary',
    }),
    monthLabel: (isCurrentMonth) => ({
      color: isCurrentMonth ? 'primary.main' : 'text.secondary',
      fontWeight: isCurrentMonth ? 600 : 400,
    }),
    bar: (value, axisMax, isCurrentMonth) => ({
      width: '100%',
      maxWidth: 36,
      height: axisMax > 0 ? `${(value / axisMax) * 85}%` : 0,
      minHeight: value > 0 ? 4 : 0,
      bgcolor: isCurrentMonth ? (theme) => alpha(theme.palette.primary.main, 0.3) : 'grey.200',
      borderRadius: '6px 6px 0 0',
      mt: 0.5,
      mb: 0.5,
    }),
  },
  upcomingSessionsContainer: {
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  // Shared outline for all Overview sections — soft 12px radius, light border, no shadow (Figma)
  overviewCard: {
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'grey.200',
    boxShadow: 'none',
  },
  overviewCardContent: {
    p: 3,
    '&:last-child': {
      pb: 3,
    },
  },
  // Figma empty states for the Overview cards (attendance history, upcoming sessions)
  overviewEmptyState: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      width: '100%',
      minHeight: 200,
      py: 5,
      px: 3,
    },
    iconCircle: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      backgroundColor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 2,
    },
    icon: {
      fontSize: 28,
      color: 'text.secondary',
    },
    title: {
      fontWeight: 600,
      color: 'text.primary',
      mb: 1,
    },
    description: {
      color: 'text.secondary',
      maxWidth: 360,
    },
    button: {
      mt: 3,
      borderRadius: '24px',
      textTransform: 'none',
      fontWeight: 600,
      color: 'text.primary',
      borderColor: 'divider',
      px: 2.5,
      '&:hover': {
        borderColor: 'text.secondary',
        backgroundColor: 'transparent',
      },
    },
  },
  // Credits card — generous spacing, large values, gift-card CTA pinned to the bottom (Figma)
  creditsCard: {
    content: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      p: 3,
    },
    title: {
      fontWeight: 600,
      mb: 4,
    },
    billingRow: {
      display: 'flex',
      gap: 4,
      mb: 4,
    },
    column: {
      flex: 1,
    },
    label: {
      fontWeight: 600,
      color: 'text.secondary',
      mb: 1,
    },
    value: {
      fontWeight: 400,
      color: 'text.primary',
      lineHeight: 1.2,
    },
    caption: {
      color: 'text.secondary',
      mt: 0.5,
    },
    claimButton: {
      mt: 'auto',
      pt: 4,
      alignSelf: 'flex-start',
      color: 'primary.main',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '16px',
      padding: 0,
      minWidth: 'auto',
      '& .MuiButton-startIcon': {
        marginRight: 1,
      },
      '& .MuiSvgIcon-root': {
        fontSize: 24,
      },
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    lockedClaimWrapper: {
      mt: 'auto',
      alignSelf: 'flex-start',
      display: 'inline-flex',
      cursor: 'not-allowed',
    },
  },
  upcomingSessionRow: {
    container: {
      display: 'flex',
      gap: 2,
      px: 2.5,
      py: 2,
      alignItems: 'flex-start',
    },
    timeColumn: {
      width: '72px',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    },
    detailsColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.25,
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: 1,
      minHeight: '64px',
    },
    statusContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
    },
    statusCheckmark: {
      color: 'success.main',
      fontSize: 12,
      mr: 0.5,
      fontWeight: 600,
    },
    waitlistPill: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: 'warning.lightest',
      color: 'warning.light',
      borderRadius: 1,
      px: 0.75,
      py: 0.125,
      fontSize: '11px',
      fontWeight: 600,
    },
    buttonGroup: {
      display: 'flex',
      gap: 1,
    },
    dateHeader: {
      backgroundColor: 'grey.50',
      px: 3,
      py: 1.75,
      borderBottom: '1px solid',
      borderColor: 'grey.200',
    },
  },
  credits: {
    mainSection: {
      mb: 3,
    },
    billingRow: {
      display: 'flex',
      justifyContent: 'space-between',
      mb: 1,
    },
    accountSection: {
      display: 'flex',
      justifyContent: 'space-between',
      mb: 1,
    },
    divider: {
      my: 2,
    },
  },
  placeholder: {
    p: 3,
    textAlign: 'center',
  },
}

export const paginationOverride = {
  mx: { xs: -2, sm: 0, md: 0 },
  '& [data-testid="pagination-container"], & [class*="pagination"]': {
    justifyContent: 'flex-start !important',
    px: { xs: 2, sm: 0, md: 0 },
  },
}

export const clientsTableWrapper = {
  ...paginationOverride,
  position: 'relative',
}

export const clientsSkeletonStyles = {
  rowContent: {
    '&:hover': { bgcolor: 'transparent' },
  },
  checkbox: {
    m: 1.5,
    flexShrink: 0,
  },
  avatar: {
    mr: 2,
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  secondaryLine: {
    mt: 0.25,
  },
  chevron: {
    ml: 1.5,
    mr: 0.5,
    flexShrink: 0,
  },
}

export const clientOverviewSkeletonStyles = {
  textLine: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  headerActionsMenu: {
    m: 1,
  },
  attendanceBar: {
    width: '100%',
    maxWidth: 36,
    borderRadius: '6px 6px 0 0',
    mt: 0.5,
    mb: 0.5,
  },
  creditValue: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  sectionTitle: {
    mb: 3,
  },
  sectionCard: {
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: 3,
    py: 2,
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    '&:last-of-type': { borderBottom: 'none' },
  },
  sectionRowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
}
